import { useMemo, useState } from 'react';
import {
  Archive,
  ArrowRight,
  Bell,
  BookOpen,
  Boxes,
  BrainCircuit,
  CalendarDays,
  Camera,
  Check,
  ChevronRight,
  Clock3,
  Cloud,
  CloudLightning,
  CloudRain,
  CloudSnow,
  Droplets,
  EyeOff,
  HardDrive,
  Leaf,
  MapPin,
  Orbit,
  RefreshCw,
  Search,
  Send,
  Server,
  ShieldCheck,
  Shirt,
  Smartphone,
  Sparkles,
  Sun,
  UsersRound,
  Warehouse,
} from 'lucide-react';
import { Store } from './mirror-store.js';
import { reflectWitnessNote } from './ai.js';
import { PrivateImage } from './private-media.jsx';
import WeatherWidget from './WeatherWidget.jsx';
import { BrandMark, profileInitial } from './branding.jsx';
import { getSurroundingAttention, getSurroundingBranch } from './surrounding-config.js';
import { matchesItemSearch } from './wardrobe-search.js';
import MirrorLens from './MirrorLens.jsx';

const WEATHER_ROLE_RULES = {
  cold: /coat|jacket|blazer|cardigan|sweater|knit|hoodie|layer|boot|wool|thermal/i,
  wet: /rain|water|shell|boot|umbrella|jacket|coat/i,
  warm: /tee|t-shirt|shirt|tank|short|skirt|linen|light|sandal/i,
};

const SEARCH_SCOPES = [
  { id: 'all', label: 'All' },
  { id: 'closet', label: 'Closet' },
  { id: 'rooms', label: 'Rooms' },
  { id: 'garden', label: 'Plants' },
  { id: 'storage', label: 'Storage' },
  { id: 'library', label: 'Reads' },
];

const WEATHER_PARTICLES = Array.from({ length: 14 }, (_, index) => index);

const SYNC_LOCATION_CLARITY = [
  {
    id: 'device',
    icon: Smartphone,
    label: 'This device',
    status: 'Working copy',
    detail: 'Mirror keeps this profile’s current workspace in browser storage so it can open quickly and continue offline. It is profile-keyed, but not separately disk-encrypted by Mirror.',
  },
  {
    id: 'service',
    icon: Server,
    label: 'Private Mirror service',
    status: 'When sync is on',
    detail: 'Closets, Surroundings, calendar, Witness notes, settings, and captured media sync to the account’s owner-gated VPS workspace. Media is not yet application-encrypted at rest.',
  },
  {
    id: 'devices',
    icon: HardDrive,
    label: 'Your other devices',
    status: 'Same profile only',
    detail: 'A device signed into this exact profile can receive the synced snapshot. Stop Sync pauses this profile on this device; it does not erase either copy.',
  },
  {
    id: 'shared',
    icon: UsersRound,
    label: 'Shared surfaces',
    status: 'Explicit actions only',
    detail: 'Public Mirror flags and Sync Party posts do not expose the whole closet. A record becomes share-eligible only when you mark or send that specific record.',
  },
];

const PRIVACY_DO = [
  'Use password-authenticated, separately scoped profile snapshots and owner checks for private media.',
  'Keep the signed-in session in this browser until you sign out or a password change invalidates it.',
  'Send only selected text, images, and needed context when you invoke Scan, Try-On, Layout, or Witness.',
  'Route AI requests through Mirror’s service so the provider key never enters the browser.',
];

const PRIVACY_DO_NOT = [
  'Create face-recognition identities, biometric templates, or identity matches from Reflector.',
  'Open the camera in the background, request microphone audio, or record the live preview.',
  'Scan contacts, unrelated files, or the rest of your photo library after you choose an image.',
  'Publish, sell, or send the full account to a party automatically.',
  'Claim medication certainty, diagnosis, or professional medical judgment.',
];

function firstName(user) {
  return String(user?.displayName || user?.username || 'there').trim().split(/\s+/)[0];
}

function greeting(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function localDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function itemText(item) {
  return [item.label, item.details?.category, item.details?.material, item.details?.season, ...(item.tags || [])].filter(Boolean).join(' ');
}

function seasonName(date = new Date()) {
  const month = date.getMonth();
  if ([11, 0, 1].includes(month)) return 'winter';
  if ([2, 3, 4].includes(month)) return 'spring';
  if ([5, 6, 7].includes(month)) return 'summer';
  return 'autumn';
}

function weatherMode(reading) {
  const condition = String(reading?.condition || '').toLowerCase();
  const temp = Number(reading?.temp);
  if (/thunder|storm/.test(condition)) return 'storm';
  if (/snow|sleet|ice/.test(condition)) return 'snow';
  if (/rain|drizzle|shower/.test(condition)) return 'rain';
  if (/clear|sun/.test(condition)) return 'clear';
  if (/cloud|overcast|fog/.test(condition)) return 'cloud';
  if (Number.isFinite(temp) && temp <= 48) return 'cold';
  return 'quiet';
}

function WeatherIcon({ mode }) {
  if (mode === 'clear') return <Sun />;
  if (mode === 'rain') return <CloudRain />;
  if (mode === 'snow') return <CloudSnow />;
  if (mode === 'storm') return <CloudLightning />;
  return <Cloud />;
}

function WeatherMotion({ mode }) {
  return <span className={`weather-motion weather-motion-${mode}`}>
    <span className="weather-sun-orb" />
    <span className="weather-cloud weather-cloud-one" />
    <span className="weather-cloud weather-cloud-two" />
    <span className="weather-lightning" />
    {WEATHER_PARTICLES.map(index => <i key={index} style={{ '--weather-index': index }} />)}
  </span>;
}

function WeatherHero({ dateLabel, mode, reading, state, user, onReading, openCapture, onOpenPanel }) {
  return <section className="reflect-weather-hero" aria-labelledby="reflect-weather-title">
    <MirrorLens
      className="reflect-home-mirror"
      prompt
      overlay={<WeatherMotion mode={mode} />}
      label="Open Reflector™ over the live weather field"
    />
    <div className="reflect-weather-copy">
      <span>{dateLabel}</span>
      <h1 id="reflect-weather-title">Reflect on today,<br /><em>surround yourself.</em></h1>
      <p>Welcome back, {firstName(user)}. Let what you keep support the day you are in.</p>
      <div className="reflect-quick-tools" aria-label="Today quick tools">
        <button type="button" onClick={openCapture}><Camera /> Capture</button>
        <button type="button" onClick={() => onOpenPanel('field')}><Orbit /> Field</button>
        <button type="button" onClick={() => onOpenPanel('intake')}><Archive /> Intake</button>
        <button type="button" onClick={() => onOpenPanel('schedule')}><CalendarDays /> Schedule</button>
        <button type="button" onClick={() => onOpenPanel('surrounding')}><Boxes /> Surroundings</button>
      </div>
    </div>
    <div className="reflect-weather-reading">
      <WeatherIcon mode={mode} />
      <strong>{reading ? `${reading.temp}°` : '—°'}</strong>
      <span>{reading?.condition || 'Set your weather'}</span>
      <WeatherWidget preferences={state.preferences} className="reflect-weather-setter" onReading={onReading} />
      <small>Reflector™ layers weather over your private live view.</small>
    </div>
  </section>;
}

function buildAgenda(state, items, date = localDateKey()) {
  const byId = new Map(items.map(item => [String(item.id), item]));
  const agenda = [];
  for (const event of state.calendar || []) {
    if (event.date !== date) continue;
    const linked = (event.itemIds || []).map(id => byId.get(String(id))).find(Boolean);
    agenda.push({
      id: `event:${event.id}`,
      title: event.title,
      detail: event.note || (event.type === 'outfit' ? 'Outfit plan' : 'On your schedule'),
      kind: event.type,
      item: linked,
      icon: event.type === 'outfit' ? Shirt : CalendarDays,
    });
  }
  const laundry = items.filter(item => item.location?.type !== 'surrounding' && item.wearStatus === 'laundry');
  if (laundry.length) agenda.push({ id: 'laundry', title: 'Laundry', detail: `${laundry.length} ${laundry.length === 1 ? 'piece' : 'pieces'} waiting`, kind: 'laundry', item: laundry[0], icon: Shirt });
  for (const item of items.filter(item => item.location?.type === 'surrounding')) {
    const attention = getSurroundingAttention(item);
    if (!attention || !['attention', 'soon'].includes(attention.level)) continue;
    const branch = getSurroundingBranch(item)?.id;
    agenda.push({
      id: `surrounding:${item.id}`,
      title: item.surrounding?.nextAction || (branch === 'garden' ? `Water ${item.label || 'plant'}` : `Check ${item.label || 'record'}`),
      detail: `${item.label || 'Surrounding'} · ${attention.label}`,
      kind: branch || 'care',
      item,
      icon: branch === 'garden' ? Droplets : branch === 'library' ? BookOpen : Boxes,
    });
  }
  return agenda.slice(0, 8);
}

function buildSuggestions(state, items, reading) {
  const history = state.mirror?.ootdHistory || [];
  const uses = new Map();
  history.forEach(fit => (fit.itemIds || []).forEach(id => uses.set(String(id), (uses.get(String(id)) || 0) + 1)));
  const linkedToday = new Set((state.calendar || []).filter(event => event.date === localDateKey()).flatMap(event => event.itemIds || []).map(String));
  const temperature = Number(reading?.temp);
  const condition = String(reading?.condition || '').toLowerCase();
  const season = seasonName();
  const mode = /rain|drizzle|shower|storm/.test(condition) ? 'wet' : Number.isFinite(temperature) && temperature <= 55 ? 'cold' : Number.isFinite(temperature) && temperature >= 75 ? 'warm' : '';
  return items
    .filter(item => (item.imageData || item.imageMediaId) && item.wearStatus !== 'laundry' && item.location?.type !== 'intake_pile')
    .map(item => {
      const text = itemText(item);
      let score = (uses.get(String(item.id)) || 0) * 20 + (item.intent?.wear ? 13 : 0) + (linkedToday.has(String(item.id)) ? 35 : 0);
      if (mode && WEATHER_ROLE_RULES[mode].test(text)) score += 24;
      if (new RegExp(season, 'i').test(text)) score += 12;
      if (item.location?.type === 'surrounding' && getSurroundingAttention(item)) score += 8;
      return { item, score };
    })
    .sort((left, right) => right.score - left.score)
    .slice(0, 10)
    .map(entry => entry.item);
}

function dateTitle(value) {
  const today = localDateKey();
  const tomorrow = localDateKey(new Date(Date.now() + 86400000));
  if (value === today) return 'Today’s plan';
  if (value === tomorrow) return 'Tomorrow’s plan';
  const date = new Date(`${value}T00:00:00`);
  return Number.isFinite(date.getTime()) ? date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' }) : 'Your plan';
}

function DateRail({ selectedDate, onSelect }) {
  const days = Array.from({ length: 7 }, (_, index) => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + index);
    return { key: localDateKey(date), day: index === 0 ? 'Today' : date.toLocaleDateString(undefined, { weekday: 'short' }), number: date.getDate(), month: date.toLocaleDateString(undefined, { month: 'short' }) };
  });
  return <section className="today-date-rail" aria-label="Choose a day for Today"><div><span>Calendar focus</span><strong>Choose a day to hold.</strong></div><div>{days.map(day => <button type="button" key={day.key} className={selectedDate === day.key ? 'active' : ''} aria-pressed={selectedDate === day.key} onClick={() => onSelect(day.key)}><small>{day.day}</small><strong>{day.number}</strong><em>{day.month}</em></button>)}</div></section>;
}

function TodayModeTabs({ mode, onChange }) {
  return <div className="today-mode-tabs" role="tablist" aria-label="Today focus"><button type="button" role="tab" aria-selected={mode === 'plan'} className={mode === 'plan' ? 'active' : ''} onClick={() => onChange('plan')}><CalendarDays /> Calendar</button><button type="button" role="tab" aria-selected={mode === 'closet'} className={mode === 'closet' ? 'active' : ''} onClick={() => onChange('closet')}><Shirt /> Clothes &amp; closet</button></div>;
}

function AgendaRail({ agenda, date, onOpenItem, onOpenPanel }) {
  return <section className="reflect-agenda" aria-labelledby="reflect-agenda-title">
    <div className="reflect-section-heading"><h2 id="reflect-agenda-title">{dateTitle(date)}</h2><button type="button" onClick={() => onOpenPanel('schedule')}>Open calendar <ArrowRight /></button></div>
    {agenda.length ? <div className="reflect-agenda-rail">{agenda.map(entry => { const Icon = entry.icon; return <button type="button" key={entry.id} onClick={() => entry.item ? onOpenItem?.(entry.item) : onOpenPanel('schedule')}><span className={`reflect-agenda-check kind-${entry.kind}`}><Icon /></span><span><strong>{entry.title}</strong><small>{entry.detail}</small></span><Check /></button>; })}</div> : <button type="button" className="reflect-agenda-empty" onClick={() => onOpenPanel('schedule')}><CalendarDays /><span><strong>This day is clear.</strong><small>Add a plan or let tracked care surface here.</small></span><ArrowRight /></button>}
  </section>;
}

function SuggestionRail({ suggestions, reading, onOpenItem, onOpenPanel }) {
  const season = seasonName();
  const context = reading ? `${reading.temp}° ${String(reading.condition || '').toLowerCase()} · ${season}` : `Favorites, tags, and ${season}`;
  return <section className="reflect-suggestions" aria-labelledby="reflect-suggestions-title">
    <div className="reflect-section-heading"><div><h2 id="reflect-suggestions-title">Suggested now</h2><span>{context}</span></div><button type="button" onClick={() => onOpenPanel('closet')}>Open closet <ArrowRight /></button></div>
    {suggestions.length ? <div className="reflect-suggestion-rail">{suggestions.map(item => <button type="button" key={`${item.location?.id}:${item.id}`} onClick={() => onOpenItem?.(item)}><span className="reflector-image"><PrivateImage source={item.imageData} mediaId={item.imageMediaId} fallback="./assets/silver-knit.webp" alt={item.label || 'Suggested record'} /></span><strong>{item.label || 'Untitled record'}</strong><small>{item.location?.type === 'surrounding' ? getSurroundingBranch(item)?.label || 'Surrounding' : item.details?.category || 'Wardrobe'}</small></button>)}</div> : <button type="button" className="reflect-suggestion-empty" onClick={() => onOpenPanel('closet')}><Sparkles /><span><strong>Your suggestion rail is learning.</strong><small>Favorite pieces, save fits, and add tags to shape it.</small></span></button>}
  </section>;
}

function Witness({ state, refresh, reading }) {
  const [note, setNote] = useState('');
  const [includePlan, setIncludePlan] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const latest = state.witness?.[0] || null;
  const submit = async event => {
    event.preventDefault();
    const trimmed = note.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await reflectWitnessNote({
        note: trimmed,
        weather: reading ? `${reading.temp}°, ${reading.condition || 'weather'}` : '',
        agenda: includePlan ? (state.calendar || []).filter(entry => entry.date === localDateKey()).map(entry => entry.title) : [],
      });
      Store.saveWitnessEntry({ note: trimmed, reflection: result.reflection, summary: result.summary, kind: result.kind, weather: reading ? `${reading.temp}° ${reading.condition || ''}` : '' });
      setNote('');
      setIncludePlan(false);
      refresh();
    } catch (reason) {
      setError(reason.message || 'Witness could not reflect this note yet.');
    } finally {
      setBusy(false);
    }
  };
  return <details className="witness-section">
    <summary className="reflect-section-heading"><div><Sparkles /><span><h2 id="witness-title">Witness</h2><small>{latest?.reflection || 'Write one note. Receive one brief reflection.'}</small></span></div><span className="witness-count">{state.witness?.length ? <><Clock3 /> {state.witness.length}</> : 'Open'} <ChevronRight /></span></summary>
    <div className="witness-paper">
      <form onSubmit={submit}><label className="sr-only" htmlFor="witness-note">Write today’s witness note</label><textarea id="witness-note" value={note} onChange={event => setNote(event.target.value)} maxLength="2000" placeholder="Write what happened. Let it leave your hand." /><label className="witness-context-choice"><input type="checkbox" checked={includePlan} onChange={event => setIncludePlan(event.target.checked)} /><span><strong>Include today’s plan titles</strong><small>Your note and current weather go to OpenAI. Plan titles go only when checked.</small></span></label><button type="submit" disabled={!note.trim() || busy}><Send /> {busy ? 'Reflecting…' : 'Witness this'}</button></form>
      <div className="witness-reflection">{latest ? <><span>{latest.kind} · {new Date(latest.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span><blockquote>{latest.reflection}</blockquote><small>{latest.summary}</small></> : <><Sparkles /><blockquote>Your first mark can be small.</blockquote><small>Witness answers with a brief original reflection, never a verdict.</small></>}</div>
      {error ? <p className="witness-error">{error}</p> : null}
    </div>
    {state.witness?.length > 1 ? <div className="witness-ledger"><div>{state.witness.slice(0, 8).map(entry => <article key={entry.id}><time>{new Date(entry.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</time><p>{entry.note}</p><small>{entry.reflection}</small></article>)}</div></div> : null}
  </details>;
}

function FocusSearch({ items, query, scope, onQuery, onScope, onOpenItem }) {
  const results = useMemo(() => {
    const needle = query.trim();
    if (!needle) return [];
    return items.filter(item => {
      const branch = getSurroundingBranch(item)?.id || '';
      if (scope === 'closet' && item.location?.type === 'surrounding') return false;
      if (scope === 'rooms' && item.location?.type !== 'surrounding') return false;
      if (['garden', 'storage', 'library'].includes(scope) && branch !== scope) return false;
      return matchesItemSearch(item, needle);
    }).slice(0, 6);
  }, [items, query, scope]);
  return <section className="focus-search" aria-label="Search Mirror">
    <label><Search /><input value={query} onChange={event => onQuery(event.target.value)} placeholder="Search everything around you…" /><kbd>⌘ K</kbd></label>
    <div className="focus-search-scopes" aria-label="Search scope"><span className="focus-search-scope-label">Search in</span>{SEARCH_SCOPES.map(entry => <button type="button" className={scope === entry.id ? 'active' : ''} aria-label={`Search scope: ${entry.label}`} aria-pressed={scope === entry.id} onClick={() => onScope(entry.id)} key={entry.id}>{entry.label}</button>)}</div>
    {query.trim() ? <div className="focus-search-results">{results.length ? results.map(item => <button type="button" key={`${item.location?.id}:${item.zone?.id}:${item.id}`} onClick={() => onOpenItem?.(item)}><span>{item.imageData || item.imageMediaId ? <PrivateImage source={item.imageData} mediaId={item.imageMediaId} fallback="./assets/silver-knit.webp" alt="" /> : <Search />}</span><span><strong>{item.label || 'Untitled record'}</strong><small>{item.location?.name || 'Mirror'} · {getSurroundingBranch(item)?.shortLabel || item.details?.category || 'Wardrobe'}</small></span><ChevronRight /></button>) : <p>No records match this view.</p>}</div> : null}
  </section>;
}

function AtGlance({ state, items, onOpenPanel }) {
  const surrounding = items.filter(item => item.location?.type === 'surrounding');
  const wardrobe = items.filter(item => item.location?.type !== 'surrounding' && item.location?.type !== 'intake_pile' && item.location?.name !== 'Unsorted Intake');
  const rooms = (state.locations || []).filter(location => location.type === 'surrounding');
  const countBranch = branch => surrounding.filter(item => getSurroundingBranch(item)?.id === branch).length;
  const cards = [
    { label: 'Closet', value: wardrobe.length, detail: 'items', icon: Shirt, panel: 'closet' },
    { label: 'Rooms', value: rooms.length, detail: 'spaces', icon: Boxes, panel: 'surrounding' },
    { label: 'Plants', value: countBranch('garden'), detail: 'alive', icon: Leaf, panel: 'surrounding', branch: 'garden', tone: 'care' },
    { label: 'Library', value: countBranch('library'), detail: 'records', icon: BookOpen, panel: 'surrounding', branch: 'library' },
    { label: 'Storage', value: countBranch('storage'), detail: 'records', icon: Warehouse, panel: 'surrounding', branch: 'storage' },
    { label: 'Calendar', value: (state.calendar || []).length, detail: 'events', icon: CalendarDays, panel: 'schedule', tone: 'reminder' },
  ];
  return <section className="focus-at-glance" aria-labelledby="focus-at-glance-title"><div className="reflect-section-heading"><h2 id="focus-at-glance-title">At a glance</h2><span>{items.length} reflected records</span></div><div>{cards.map(card => { const Icon = card.icon; return <button type="button" className={card.tone ? `tone-${card.tone}` : ''} onClick={() => onOpenPanel(card.panel, card.branch)} key={card.label}><span><Icon /> {card.label}</span><strong>{card.value}</strong><small>{card.detail}</small></button>; })}</div></section>;
}

function SyncLocation({ entry }) {
  const Icon = entry.icon;
  return <article className="intelligence-location-row"><Icon /><div><span><strong>{entry.label}</strong><em>{entry.status}</em></span><p>{entry.detail}</p></div></article>;
}

function MirrorIntelligence({ sync, onOpenPanel, state }) {
  const weatherSource = state.preferences?.weatherSource;
  const locationClarity = weatherSource === 'geolocation'
    ? 'Weather location: when weather refreshes, the browser supplies current coordinates to Mirror’s weather route. Coordinates are not saved in the Mirror snapshot.'
    : weatherSource === 'zip' && state.preferences?.weatherZip
      ? `Weather location: ${state.preferences.weatherZip} is saved as a synced preference. GPS is not requested in zip mode.`
      : 'Weather location: off until you choose GPS or enter a zip. Room names are labels you create—not physical tracking.';
  return <section className="focus-intelligence" aria-labelledby="focus-intelligence-title">
    <div className="reflect-section-heading"><h2 id="focus-intelligence-title">Mirror intelligence</h2><span>Consent first</span></div>
    <div className="focus-intelligence-grid">
      <div className="focus-reflector-card"><MirrorLens prompt label="Open Reflector™ as a private live mirror" /></div>
      <div className="focus-intelligence-shelf">
        <details className="reflector-trust-shelf intelligence-clarity-shelf" name="mirror-intelligence-clarity"><summary><RefreshCw /><span><strong>Sync &amp; devices</strong><small>{sync?.label || 'Review your connection'} · where data lives</small></span><ChevronRight /></summary><div className="intelligence-clarity-body">
          <div className={`intelligence-sync-status state-${sync?.state || 'local'}`}><span>Current state</span><strong>{sync?.label || 'Saved on this device'}</strong><small>{sync?.detail || 'Your working copy remains available here.'}</small></div>
          <div className="intelligence-location-list">{SYNC_LOCATION_CLARITY.map(entry => <SyncLocation entry={entry} key={entry.id} />)}</div>
          <div className="intelligence-location-note"><MapPin /><p>{locationClarity}</p></div>
          <div className="intelligence-not-synced"><EyeOff /><div><strong>Not part of normal sync</strong><small>Reflector’s live camera feed, microphone audio, contacts, unrelated files, and an unselected photo library.</small></div></div>
          <button type="button" className="intelligence-profile-link" onClick={() => onOpenPanel('profile')}>Open sync controls <ChevronRight /></button>
        </div></details>
        <details className="reflector-trust-shelf" name="mirror-intelligence-clarity"><summary><ShieldCheck /><span><strong>Reflector™ identity boundary</strong><small>Camera permission, provenance, and privacy</small></span><ChevronRight /></summary><div><p><strong>Live means live.</strong> Reflector requests camera access only after your tap, never requests audio, never records the preview, and stops when the app is hidden.</p><p><strong>Saved is explicit.</strong> A captured photo is a separate action and follows the sync choice shown in your profile.</p><p><strong>Browser enforced.</strong> Mirror blocks microphone access and cross-site camera embedding at the site boundary.</p></div></details>
        <details className="reflector-trust-shelf intelligence-clarity-shelf privacy-clarity-shelf" name="mirror-intelligence-clarity"><summary><ShieldCheck /><span><strong>Privacy &amp; identity</strong><small>A proper account of what we do—and do not do</small></span><ChevronRight /></summary><div className="intelligence-clarity-body">
          <div className="identity-definition"><BrainCircuit /><div><strong>Account identity, not biometric identity.</strong><p>Mirror knows the signed-in profile and the records that profile creates. Reflector is a live view, not a face-identification system.</p></div></div>
          <div className="privacy-commitment-grid"><section><span>What Mirror does</span><ul>{PRIVACY_DO.map(item => <li key={item}>{item}</li>)}</ul></section><section><span>What Mirror does not do</span><ul>{PRIVACY_DO_NOT.map(item => <li key={item}>{item}</li>)}</ul></section></div>
          <p className="privacy-provider-note"><strong>Third-party boundary.</strong> Weather requests use Open-Meteo through Mirror. Chosen AI actions use the configured OpenAI service. Normal browsing and the live Reflector preview do not send camera frames to either provider.</p>
          <button type="button" className="intelligence-profile-link" onClick={() => onOpenPanel('profile')}>Open profile &amp; sharing controls <ChevronRight /></button>
        </div></details>
      </div>
    </div>
  </section>;
}

export default function ReflectiveToday({ state, items, user, sync, openCapture, onOpenPanel, onOpenItem, refresh }) {
  const [reading, setReading] = useState(null);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState('all');
  const [selectedDate, setSelectedDate] = useState(() => localDateKey());
  const [todayMode, setTodayMode] = useState('plan');
  const agenda = useMemo(() => buildAgenda(state, items, selectedDate), [items, selectedDate, state]);
  const suggestions = useMemo(() => buildSuggestions(state, items, reading), [items, reading, state]);
  const mode = weatherMode(reading);
  const dateLabel = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  return <main className={`reflective-today focus-dashboard weather-${mode}`}>
    <header className="reflect-mobile-header"><BrandMark /><span><button type="button" onClick={() => onOpenPanel('schedule')} aria-label="Open schedule"><Bell /></button><button type="button" onClick={() => onOpenPanel('profile')} aria-label="Open profile">{profileInitial(user)}</button></span></header>
    <WeatherHero dateLabel={dateLabel} mode={mode} reading={reading} state={state} user={user} onReading={setReading} openCapture={openCapture} onOpenPanel={onOpenPanel} />
    <DateRail selectedDate={selectedDate} onSelect={setSelectedDate} />
    <TodayModeTabs mode={todayMode} onChange={setTodayMode} />
    {todayMode === 'plan' ? <div className="focus-dashboard-grid"><div className="focus-dashboard-main"><AgendaRail agenda={agenda} date={selectedDate} onOpenItem={onOpenItem} onOpenPanel={onOpenPanel} /><Witness state={state} refresh={refresh} reading={reading} /></div><aside className="focus-dashboard-aside"><AtGlance state={state} items={items} onOpenPanel={onOpenPanel} /><MirrorIntelligence sync={sync} onOpenPanel={onOpenPanel} state={state} /></aside></div> : <section className="today-closet-focus" aria-label="Clothes and closet"><FocusSearch items={items} query={query} scope={scope} onQuery={setQuery} onScope={setScope} onOpenItem={onOpenItem} /><SuggestionRail suggestions={suggestions} reading={reading} onOpenItem={onOpenItem} onOpenPanel={onOpenPanel} /></section>}
  </main>;
}
